import Link from "next/link";
import Image from "next/image";

const tips = [
  {
    title: "Lavage à la main",
    text: "Privilégie toujours un lavage à la main, à froid ou légèrement tiède, avec une lessive douce adaptée à la laine.",
  },
  {
    title: "Pas de frottements excessifs",
    text: "Évite de tordre, frotter ou laisser tremper trop longtemps la fibre afin de préserver la douceur et la structure du fil.",
  },
  {
    title: "Séchage à plat",
    text: "Après rinçage, presse délicatement l’excédent d’eau dans une serviette propre puis laisse sécher à plat, loin d’une source de chaleur directe.",
  },
  {
    title: "Rangement soigné",
    text: "Conserve les créations et les pelotes à l’abri de la lumière directe, de l’humidité et des frottements trop répétés.",
  },
];

const careSteps = [
  {
    step: "01",
    title: "Préparer le bain",
    text: "Utilise une eau froide ou légèrement tiède avec une noisette de lessive spéciale laine. Mélange doucement avant d’y plonger la pièce.",
  },
  {
    step: "02",
    title: "Laver sans brusquer",
    text: "Laisse la matière s’imbiber tranquillement puis presse-la délicatement avec les mains, sans torsion ni mouvement trop énergique.",
  },
  {
    step: "03",
    title: "Rincer avec douceur",
    text: "Rince à température similaire pour éviter les chocs thermiques, jusqu’à ce que l’eau soit claire.",
  },
  {
    step: "04",
    title: "Sécher à plat",
    text: "Roulez la pièce dans une serviette pour retirer l’excès d’eau, reforme-la puis laisse-la sécher à plat sur une surface propre.",
  },
];

const faq = [
  {
    question: "Les fils avec paillettes demandent-ils plus d’attention ?",
    answer:
      "Oui, légèrement. Le geste reste le même, mais un lavage encore plus doux permet de préserver l’éclat et la tenue des fibres fantaisie.",
  },
  {
    question: "Puis-je laver en machine avec un programme laine ?",
    answer:
      "Par précaution, il est préférable de rester sur un lavage à la main. C’est la solution la plus sûre pour conserver la beauté des teintes et la qualité du fil.",
  },
  {
    question: "Comment éviter que la pièce se déforme ?",
    answer:
      "Le plus important est d’éviter de suspendre la pièce mouillée. Le séchage à plat permet de garder une forme nette et régulière.",
  },
];

export default function CareInstructionsPage() {
  return (
    <main className="bg-[var(--background)] text-[var(--foreground)]">
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute top-[-120px] left-[-80px] h-[420px] w-[420px] rounded-full bg-[#f4d7e7]/40 blur-[120px]" />
        <div className="absolute bottom-[-160px] right-[-100px] h-[480px] w-[480px] rounded-full bg-[var(--primary)]/20 blur-[140px]" />
        <div className="absolute top-[40%] left-[50%] h-[300px] w-[300px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/40 blur-[100px]" />
      </div>
      <section className="section pt-10 sm:pt-12 lg:pt-16">
        <div className="mx-auto grid max-w-[1400px] gap-8 px-6 sm:px-8 lg:grid-cols-[minmax(0,1.08fr)_320px] lg:items-end lg:px-10 xl:px-14">
          <div className="relative overflow-hidden rounded-[40px] border border-[var(--border)] bg-[linear-gradient(140deg,rgba(255,255,255,0.72),rgba(244,215,231,0.55))] px-6 py-6 shadow-[0_30px_80px_rgba(50,35,35,0.05)] sm:px-8 sm:py-7 lg:px-10 lg:py-9">
            <p className="font-[family-name:var(--font-ibm-plex-mono)] text-[11px] uppercase tracking-[0.22em] text-[var(--accent)]/80">
              Conseils d&apos;entretien
            </p>
            <div className="mt-4 space-y-4">
              <h1 className="max-w-[13ch] text-[2.1rem] font-semibold leading-[0.92] tracking-[-0.06em] sm:max-w-none sm:text-[2.8rem] lg:text-[3.6rem]">
                Prendre soin des pièces et des fils teints à la main
              </h1>
              <p className="max-w-[62ch] text-[0.95rem] leading-[1.65] text-[var(--foreground)]/72 sm:text-base">
                Chaque création mérite un entretien simple, doux et régulier.
                Ces gestes permettent de préserver les nuances, la souplesse du
                fil et la beauté du travail artisanal dans le temps.
              </p>
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                href="/boutique"
                className="inline-flex items-center justify-center rounded-full bg-[var(--primary)] px-5 py-3 text-sm font-medium text-white shadow-[0_14px_30px_rgba(255,63,142,0.18)] transition duration-300 hover:-translate-y-0.5 hover:opacity-95"
              >
                Voir la boutique
              </Link>
              <Link
                href="/contact"
                className="inline-flex items-center justify-center rounded-full border border-[var(--border)] bg-white/70 px-5 py-3 text-sm font-medium transition duration-300 hover:border-[var(--primary)] hover:text-[var(--primary)]"
              >
                Poser une question
              </Link>
            </div>
            <div className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-white/30 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-12 left-10 h-32 w-32 rounded-full bg-[#f4d7e7]/40 blur-2xl" />
            <div className="pointer-events-none absolute right-[-40px] bottom-[-40px] opacity-40 mix-blend-multiply">
              <Image
                src="/images/7.webp"
                alt="Texture laine"
                width={300}
                height={300}
                className="object-contain"
              />
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-1">
            <div className="rounded-[24px] border border-[var(--border)] bg-[#f4d7e7]/80 backdrop-blur px-4 py-4 shadow-[0_14px_26px_rgba(50,35,35,0.035)]">
              <p className="font-[family-name:var(--font-ibm-plex-mono)] text-[11px] uppercase tracking-[0.22em] text-[var(--accent)]/82">
                Température
              </p>
              <p className="mt-3 text-[1.6rem] font-semibold tracking-[-0.05em]">
                Froid à tiède
              </p>
            </div>
            <div className="rounded-[24px] border border-[var(--border)] bg-[#f4d7e7]/80 backdrop-blur px-4 py-4 shadow-[0_14px_26px_rgba(50,35,35,0.035)]">
              <p className="font-[family-name:var(--font-ibm-plex-mono)] text-[11px] uppercase tracking-[0.22em] text-[var(--accent)]/82">
                Méthode
              </p>
              <p className="mt-3 text-[1.6rem] font-semibold tracking-[-0.05em]">
                Lavage main
              </p>
            </div>
            <div className="rounded-[24px] border border-[var(--border)] bg-[#f4d7e7]/80 backdrop-blur px-4 py-4 shadow-[0_14px_26px_rgba(50,35,35,0.035)]">
              <p className="font-[family-name:var(--font-ibm-plex-mono)] text-[11px] uppercase tracking-[0.22em] text-[var(--accent)]/82">
                Séchage
              </p>
              <p className="mt-3 text-[1.6rem] font-semibold tracking-[-0.05em]">
                À plat
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="section pt-6 sm:pt-8 lg:pt-10">
        <div className="mx-auto max-w-[1400px] px-6 sm:px-8 lg:px-10 xl:px-14">
          <div className="grid gap-5 lg:grid-cols-[minmax(0,0.92fr)_minmax(0,1.08fr)] lg:items-stretch">
            <article className="relative overflow-hidden rounded-[34px] border border-[var(--border)] bg-[linear-gradient(145deg,rgba(244,215,231,0.52),rgba(255,255,255,0.74))] px-6 py-6 shadow-[0_24px_54px_rgba(50,35,35,0.05)] sm:px-7 sm:py-7">
              <div className="pointer-events-none absolute -right-10 top-0 h-40 w-40 rounded-full bg-[var(--primary)]/10 blur-3xl" />
              <p className="font-[family-name:var(--font-ibm-plex-mono)] text-[11px] uppercase tracking-[0.22em] text-[var(--accent)]/82">
                Le bon geste
              </p>
              <div className="mt-4 space-y-4">
                <h2 className="max-w-[11ch] text-[2rem] font-semibold leading-[0.94] tracking-[-0.05em] sm:max-w-none sm:text-[2.5rem]">
                  Une fibre vivante mérite des gestes simples et délicats
                </h2>
                <p className="max-w-[54ch] text-[0.98rem] leading-[1.75] text-[var(--foreground)]/72">
                  L’entretien ne demande pas de technique compliquée, seulement
                  un peu de douceur, de régularité et d’attention. L’idée n’est
                  pas d’en faire trop, mais de préserver l’éclat des teintes,
                  la souplesse du fil et la tenue naturelle de la pièce.
                </p>
              </div>
              <div className="mt-8 grid gap-4">
                <div className="relative overflow-hidden rounded-[26px] border border-[var(--border)] bg-white/56 shadow-[0_18px_36px_rgba(50,35,35,0.04)]">
                  <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.02),rgba(36,24,29,0.14))]" />
                  <Image
                    src="/images/7.webp"
                    alt="Texture douce de laine teinte à la main"
                    width={1200}
                    height={800}
                    className="h-[240px] w-full object-cover sm:h-[300px]"
                  />
                  <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-3 p-4 sm:p-5">
                    <div className="max-w-[26ch] rounded-[18px] border border-white/20 bg-[rgba(255,255,255,0.16)] px-4 py-3 backdrop-blur-md">
                      <p className="font-[family-name:var(--font-ibm-plex-mono)] text-[10px] uppercase tracking-[0.2em] text-white/82">
                        Matière & douceur
                      </p>
                      <p className="mt-2 text-sm leading-6 text-white/88">
                        Une attention simple suffit à préserver la main, le tombé et la lumière des nuances.
                      </p>
                    </div>
                    <span className="hidden rounded-full border border-white/24 bg-[rgba(255,255,255,0.16)] px-3 py-1.5 font-[family-name:var(--font-ibm-plex-mono)] text-[10px] uppercase tracking-[0.18em] text-white/82 backdrop-blur md:inline-flex">
                      Geste délicat
                    </span>
                  </div>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="rounded-[22px] border border-[var(--border)] bg-white/62 px-4 py-4 backdrop-blur">
                    <p className="font-[family-name:var(--font-ibm-plex-mono)] text-[10px] uppercase tracking-[0.2em] text-[var(--accent)]/78">
                      À privilégier
                    </p>
                    <p className="mt-2 text-sm leading-7 text-[var(--foreground)]/74">
                      Eau froide à tiède, lavage main, séchage à plat et gestes
                      lents.
                    </p>
                  </div>
                  <div className="rounded-[22px] border border-[var(--border)] bg-white/62 px-4 py-4 backdrop-blur">
                    <p className="font-[family-name:var(--font-ibm-plex-mono)] text-[10px] uppercase tracking-[0.2em] text-[var(--accent)]/78">
                      À éviter
                    </p>
                    <p className="mt-2 text-sm leading-7 text-[var(--foreground)]/74">
                      Torsion, chaleur directe, essorage agressif et suspension en
                      position mouillée.
                    </p>
                  </div>
                </div>
              </div>
            </article>

            <div className="grid gap-4 md:grid-cols-2">
              {tips.map((tip, index) => (
                <article
                  key={tip.title}
                  className={[
                    "group relative overflow-hidden rounded-[28px] border border-[var(--border)] px-5 py-5 transition duration-300",
                    index === 0
                      ? "bg-[linear-gradient(145deg,rgba(255,255,255,0.82),rgba(244,215,231,0.42))] shadow-[0_18px_36px_rgba(50,35,35,0.04)] md:col-span-2"
                      : "bg-white/62 backdrop-blur shadow-[0_14px_26px_rgba(50,35,35,0.03)] hover:-translate-y-1 hover:shadow-[0_22px_42px_rgba(50,35,35,0.055)]",
                  ].join(" ")}
                >
                  <div className="pointer-events-none absolute right-0 top-0 h-24 w-24 rounded-full bg-[#f4d7e7]/35 blur-2xl transition duration-300 group-hover:scale-110" />
                  <div className="relative">
                    <p className="font-[family-name:var(--font-ibm-plex-mono)] text-[11px] uppercase tracking-[0.18em] text-[var(--accent)]/78">
                      {index === 0 ? "Base essentielle" : "Repère"}
                    </p>
                    <h2 className="mt-3 text-[1.22rem] font-semibold tracking-[-0.04em] sm:text-[1.28rem]">
                      {tip.title}
                    </h2>
                    <p className="mt-2 max-w-[34ch] text-sm leading-7 text-[var(--foreground)]/72">
                      {tip.text}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="section pt-8 sm:pt-10 lg:pt-12">
        <div className="mx-auto max-w-[1400px] px-6 sm:px-8 lg:px-10 xl:px-14">
          <div className="grid gap-6 lg:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] lg:items-start">
            <div className="space-y-4 lg:sticky lg:top-28">
              <p className="font-[family-name:var(--font-ibm-plex-mono)] text-[11px] uppercase tracking-[0.22em] text-[var(--accent)]/82">
                Routine conseillée
              </p>
              <h2 className="text-[2rem] font-semibold leading-[0.96] tracking-[-0.05em] sm:text-[2.7rem]">
                Un rituel plus fluide, plus vivant et plus facile à retenir
              </h2>
              <p className="max-w-[46ch] text-[0.98rem] leading-[1.75] text-[var(--foreground)]/72">
                Plutôt qu’une suite de blocs identiques, voici une lecture plus
                intuitive : un enchaînement simple, presque comme une chorégraphie
                douce, pour accompagner la pièce du bain jusqu’au séchage.
              </p>
              <div className="rounded-[24px] border border-[var(--border)] bg-[#f4d7e7]/72 px-5 py-5 backdrop-blur shadow-[0_16px_30px_rgba(50,35,35,0.035)]">
                <p className="font-[family-name:var(--font-ibm-plex-mono)] text-[10px] uppercase tracking-[0.2em] text-[var(--accent)]/78">
                  Fil conducteur
                </p>
                <p className="mt-2 text-sm leading-7 text-[var(--foreground)]/72">
                  Toujours conserver une température stable, manipuler la matière
                  sans tension et lui laisser le temps de reprendre sa forme à
                  plat.
                </p>
              </div>
            </div>

            <div className="relative pl-0 lg:pl-8">
              <div className="pointer-events-none absolute left-[20px] top-0 hidden h-full w-px bg-gradient-to-b from-[var(--accent)]/16 via-[var(--primary)]/26 to-transparent lg:block" />
              <div className="space-y-4">
                {careSteps.map((item, index) => (
                  <article
                    key={item.step}
                    className={[
                      "relative overflow-hidden rounded-[28px] border border-[var(--border)] px-5 py-5 shadow-[0_16px_32px_rgba(50,35,35,0.035)] transition duration-300 hover:-translate-y-1",
                      index % 2 === 0
                        ? "bg-[linear-gradient(145deg,rgba(255,255,255,0.88),rgba(249,236,242,0.84))]"
                        : "bg-[linear-gradient(145deg,rgba(244,215,231,0.34),rgba(255,255,255,0.84))]",
                      "lg:ml-10",
                    ].join(" ")}
                  >
                    <div className="pointer-events-none absolute -right-8 -top-8 h-24 w-24 rounded-full bg-[#f4d7e7]/32 blur-2xl" />
                    <div className="relative flex flex-col gap-4 sm:flex-row sm:items-start">
                      <div className="flex items-center gap-3 sm:w-[120px] sm:flex-col sm:items-start sm:gap-2">
                        <span className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-[rgba(217,90,47,0.18)] bg-white/75 font-[family-name:var(--font-ibm-plex-mono)] text-[12px] tracking-[0.16em] text-[var(--accent)]/84 shadow-[0_10px_20px_rgba(50,35,35,0.04)]">
                          {item.step}
                        </span>
                        <span className="text-[0.8rem] uppercase tracking-[0.18em] text-[var(--foreground)]/38 sm:hidden">
                          Étape
                        </span>
                      </div>

                      <div className="min-w-0 flex-1">
                        <h3 className="text-[1.35rem] font-semibold tracking-[-0.04em] sm:text-[1.45rem]">
                          {item.title}
                        </h3>
                        <p className="mt-2 max-w-[46ch] text-sm leading-7 text-[var(--foreground)]/72 sm:text-[0.98rem]">
                          {item.text}
                        </p>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section pb-12 pt-8 sm:pb-14 sm:pt-10 lg:pb-16 lg:pt-12">
        <div className="mx-auto grid max-w-[1400px] gap-6 px-6 sm:px-8 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)] lg:px-10 xl:px-14">
          <div className="relative overflow-hidden rounded-[32px] border border-[var(--border)] bg-white/72 px-6 py-6 shadow-[0_18px_34px_rgba(50,35,35,0.035)] sm:px-7 sm:py-7">
            <p className="font-[family-name:var(--font-ibm-plex-mono)] text-[11px] uppercase tracking-[0.22em] text-[var(--accent)]/82">
              Questions fréquentes
            </p>
            <div className="mt-5 space-y-4">
              {faq.map((item) => (
                <article
                  key={item.question}
                  className="rounded-[20px] border border-[var(--border)] bg-[rgba(244,215,231,0.22)] px-4 py-4 transition duration-200 hover:bg-[rgba(244,215,231,0.35)]"
                >
                  <h3 className="text-[1.02rem] font-medium tracking-[-0.03em]">
                    {item.question}
                  </h3>
                  <p className="mt-2 text-sm leading-7 text-[var(--foreground)]/72">
                    {item.answer}
                  </p>
                </article>
              ))}
            </div>
            <div className="pointer-events-none absolute -top-10 right-0 h-40 w-40 rounded-full bg-[#f4d7e7]/30 blur-3xl" />
          </div>

          <aside className="rounded-[30px] border border-[var(--border)] bg-[#f4d7e7]/90 backdrop-blur px-6 py-6 shadow-[0_22px_50px_rgba(50,35,35,0.045)] sm:px-7 sm:py-7">
            <p className="font-[family-name:var(--font-ibm-plex-mono)] text-[11px] uppercase tracking-[0.22em] text-[var(--accent)]/82">
              À retenir
            </p>
            <div className="mt-4 space-y-4">
              <h2 className="text-[1.8rem] font-semibold leading-[0.98] tracking-[-0.05em]">
                Un entretien doux prolonge naturellement la vie des créations
              </h2>
              <p className="text-sm leading-7 text-[var(--foreground)]/72 sm:text-base">
                Si un doute subsiste sur une composition, un métrage ou une
                manière d&apos;entretenir une pièce, il est toujours possible de me
                contacter avant ou après achat.
              </p>
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                href="/contact"
                className="inline-flex items-center justify-center rounded-full bg-[var(--primary)] px-5 py-3 text-sm font-medium text-white shadow-[0_14px_30px_rgba(255,63,142,0.18)] transition duration-300 hover:-translate-y-0.5 hover:opacity-95"
              >
                Me contacter
              </Link>
              <Link
                href="/conditions-generales-de-vente"
                className="inline-flex items-center text-sm font-medium text-[var(--foreground)]/74 underline decoration-[rgba(36,24,29,0.18)] underline-offset-4 transition hover:text-[var(--foreground)] hover:decoration-[rgba(36,24,29,0.42)]"
              >
                Consulter les conditions de vente
              </Link>
            </div>
          </aside>
        </div>
      </section>
    </main>
  );
}